import React, { useState } from 'react';
import { message, Modal, Form, Input, Button, Tooltip, Popconfirm } from 'antd';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import ClientCompanyAPI from '../api/ClientCompanyAPI';
import ClientCompany, { MinimalClientCompany } from '../types/ClientCompany';
import ConditionalParent from '../../components/ConditionalParent';
import { userHasPermission } from '../../user/userUtils';
import { Permission } from '../../user/types/Permission';
import { useUser } from '../../user/userContext';

type EditClientCompanyModalProps = {
  bookingOrderId?: number;
  clientCompany: MinimalClientCompany;
  visible: boolean;
  disableDelete?: boolean;
  onUpdate?: (clientCompany: MinimalClientCompany) => void;
  onDelete?: () => void;
  onCancel?: () => void;
};

const EditClientCompanyModal: React.FC<EditClientCompanyModalProps> = ({
  bookingOrderId,
  clientCompany,
  visible,
  disableDelete,
  onUpdate,
  onDelete,
  onCancel,
}) => {
  const [currentUser] = useUser();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);

  const { t } = useTranslation();

  const submit = () => {
    form.submit();
  };

  const onFinish = async (values: ClientCompany) => {
    setSubmitting(true);
    try {
      const { data } = await ClientCompanyAPI.updateClientCompany(
        clientCompany.id,
        {
          name: values.name,
          elsaClientCompanyId: clientCompany.elsaClientCompanyId,
        },
      );
      onUpdate?.(data);
    } catch (error) {
      if (isAxiosError(error) && !error.response?.data.message) {
        message.error(t('errors.somethingWentWrong'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const deleteClientCompany = async () => {
    if (disableDelete) return;
    setSubmitting(true);
    try {
      await ClientCompanyAPI.deleteBookingOrderClientCompanyDryRun(
        clientCompany.id,
        bookingOrderId ?? null,
      );
      onDelete?.();
    } catch (error) {
      if (isAxiosError(error) && !error.response?.data.message) {
        message.error(t('errors.somethingWentWrong'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const showDeleteButton = userHasPermission(
    currentUser,
    Permission.CLIENT_COMPANY_DELETE,
  );

  return (
    <Modal
      title={t('components.EditClientCompanyModal.editCompany')}
      open={visible}
      onCancel={onCancel}
      onOk={submit}
      footer={[
        <Button key="back" onClick={onCancel}>
          {t('common.close')}
        </Button>,
        ...(showDeleteButton
          ? [
              <ConditionalParent
                key="delete"
                condition={disableDelete}
                parent={(children) => (
                  <Tooltip
                    title={t(
                      'components.EditClientCompanyModal.mustSaveBeforeAnotherDelete',
                    )}>
                    {children}
                  </Tooltip>
                )}>
                <Popconfirm
                  className="ml-2"
                  disabled={disableDelete}
                  title={
                    <div className="flex flex-col">
                      <p>
                        {t('components.EditClientCompanyModal.confirmDelete')}
                        &nbsp;
                      </p>
                    </div>
                  }
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                  onConfirm={deleteClientCompany}
                  placement="top">
                  <Button
                    type="primary"
                    danger
                    loading={submitting}
                    disabled={disableDelete}>
                    {t('common.delete')}
                  </Button>
                </Popconfirm>
              </ConditionalParent>,
            ]
          : []),
        <Button
          key="submit"
          className="ml-2"
          type="primary"
          loading={submitting}
          form="editClientCompanyForm"
          htmlType="submit">
          {t('common.save')}
        </Button>,
      ]}
      maskClosable={false}>
      <Form
        id="editClientCompanyForm"
        form={form}
        layout="vertical"
        onFinish={(values) => onFinish(values as ClientCompany)}
        initialValues={{
          name: clientCompany.name,
        }}>
        <Form.Item
          name="name"
          label={t('common.name')}
          rules={[
            {
              required: true,
              message: t('components.EditClientCompanyModal.fillOutName'),
            },
          ]}>
          <Input placeholder={t('common.name')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditClientCompanyModal;
